import * as dates from "common/dates";
import * as numbers from "common/numbers";
import { flattenKeys } from "common/objects";
import { extractNameFromEmail } from "common/strings";
import moment from "moment";
import { useCallback, useState } from "react";
import { useSelector } from "react-redux";
import * as docGenService from "services/docGenService";
import * as generatedDocumentsService from "services/generatedDocumentsService";
import * as submissionsService from "services/submissionsService";
import * as peopleSelectors from "store/selectors/people/peopleSelectors";
import * as persistenceSelectors from "store/selectors/persistence/persistenceSelectors";
import * as pricingSelectors from "store/selectors/pricing/pricingSelectors";
import * as submissionSelectors from "store/selectors/submission/submissionSelector";
import * as userSelectors from "store/selectors/user/userSelectors";

const OUR_LAYER_KEYS = new Set([
  "aggregateLimit",
  "facPremium",
  "grossPremium",
  "limit",
  "lineSize",
  "netPuniWrap",
  "puniWrap",
  "puniWrapBrokerage",
]);

export const computePricing = ({ program, baseLayerBurn } = {}) => {
  const xsTotals = program?.xsRatingResult?.totals;
  const glTotals = baseLayerBurn?.pricing?.values?.totals;
  return {
    technicalPrice: xsTotals?.fses_technical ?? glTotals?.total_cgl ?? null,
    chargedPremium:
      xsTotals?.charged_premium ?? glTotals?.charged_premium ?? null,
  };
};

export const useGetTemplateSummary = ({ layerIndex }) => {
  const [
    getTemplateSummary,
    {
      isLoading: isTemplateSummaryDownloading,
      isError: isTemplateSummaryError,
    },
  ] = docGenService.useGetTemplateSummaryMutation();

  const [, makeJinjaContent] = useGatherData({ layerIndex });

  const downloadSummary = async ({
    template,
    overrideResolvedSubmission = null,
  }) => {
    return getTemplateSummary({
      templateId: template.id,
      templateForm: template.form,
      content: makeJinjaContent({ overrideResolvedSubmission }),
    });
  };

  return {
    downloadSummary,
    isTemplateSummaryDownloading,
    isTemplateSummaryError,
  };
};

const useGatherData = ({ layerIndex }) => {
  const defaultResolvedSubmission = useSelector(
    submissionSelectors.selectResolvedSubmissionWithoutInput
  );
  const activePolicyId = useSelector(pricingSelectors.getActivePolicyId);
  const underwriter = useSelector(peopleSelectors.selectPreferredUnderwriter);
  const broker = useSelector(peopleSelectors.selectBroker);
  const makeJinjaContent = ({ overrideResolvedSubmission } = {}) => {
    const resolvedSubmission =
      overrideResolvedSubmission ?? defaultResolvedSubmission;
    return {
      submission: resolvedSubmission,
      layerId: resolvedSubmission.layers[layerIndex].id,
      policyId: activePolicyId ?? undefined,
    };
  };
  const makeContent = ({ overrideResolvedSubmission } = {}) => {
    const resolvedSubmission =
      overrideResolvedSubmission ?? defaultResolvedSubmission;
    const formatDate = (format) => (date) =>
      date ? moment.parseZone(date).format(format) : "";

    const program = {
      ...resolvedSubmission.program,
      brokerHouse: broker?.company,
      broker: broker?.name,
      brokerContact: broker?.email,
      brokerId: broker?.id,
      preferredUnderwriter: underwriter?.name,
      preferredUnderwriterId: underwriter?.id,
      inceptionYear: formatDate("yyyy")(resolvedSubmission?.program?.inception),
    };
    const layer = resolvedSubmission.layers?.[layerIndex] ?? {};
    const ourLayer = Number.isFinite(layer?.shareOfLine)
      ? Object.fromEntries(
          Object.entries(layer)
            .filter(([k, v]) => OUR_LAYER_KEYS.has(k) && Number.isFinite(v))
            .map(([k, v]) => [k, v * layer.shareOfLine * 0.01])
        )
      : {};

    const formatDnfDates = formatDate("DD-MMMM-yyyy");

    const pricing = computePricing({
      program,
      baseLayerBurn: resolvedSubmission.baseLayerBurn,
    });

    return flattenKeys({
      program,
      layers: resolvedSubmission.layers,
      layer,
      ourLayer,
      pricing,
      tower: resolvedSubmission.tower?.layers,
      dnf: {
        today: formatDnfDates(dates.getDate()),
        inception: formatDnfDates(program.inception),
        expiration: formatDnfDates(program.expiration),
        perils: "",
        ourLimit: numbers.currencyFormat(ourLayer.limit),
        ourGrossPremium: numbers.currencyFormat(ourLayer.grossPremium),
        ourTriaAmount: numbers.currencyFormat(layer.TRIAAmount),
        limit: numbers.currencyFormat(layer.limit),
        attachment: numbers.currencyFormat(layer.attachment),
        grossPremium: numbers.currencyFormat(layer.grossPremium),
        triaAmount:
          layer.TRIAAmount != null && layer.shareOfLine
            ? numbers.currencyFormat(
                layer.TRIAAmount / (layer.shareOfLine / 100)
              )
            : "",
        tiv: numbers.currencyFormat(resolvedSubmission.programPricing?.TIV),
      },
    });
  };
  return [makeContent, makeJinjaContent];
};

export const useDownloadTemplate = ({ layerIndex }) => {
  const submissionId = useSelector(persistenceSelectors.selectSubmissionId);
  const resolvedSubmission = useSelector(
    submissionSelectors.selectResolvedSubmissionWithoutInput
  );
  const userEmail = useSelector(userSelectors.selectUserEmail);
  const savedBy = useSelector((state) => state.persistence?.savedBy);
  const lastSaved = useSelector((state) => state.persistence?.lastSaved);

  const { data: versions } = submissionsService.useListVersionsQuery(
    { submissionId: submissionId },
    { skip: !submissionId }
  );

  const [
    fillTemplate,
    { isLoading: isDownloading, isError },
  ] = docGenService.useFillTemplateMutation();

  const [
    saveDocument,
  ] = generatedDocumentsService.useSaveGeneratedDocumentMutation();

  const [getTemplateSummary] = docGenService.useGetTemplateSummaryMutation();

  const [makeContent, makeJinjaContent] = useGatherData({ layerIndex });

  // What the last fill of each template came back with, keyed by template
  // id: `{ warnings }` when it generated (possibly with values that printed
  // as blank) or `{ error }` when it did not. The screen shows this beside
  // the template until the user dismisses it with `clearOutcome`.
  const [lastOutcome, setLastOutcome] = useState({});

  const recordOutcome = useCallback((templateId, outcome) => {
    setLastOutcome((previous) => ({ ...previous, [templateId]: outcome }));
  }, []);

  const clearOutcome = useCallback((templateId) => {
    setLastOutcome((previous) => {
      if (!(templateId in previous)) {
        return previous;
      }
      const { [templateId]: _removed, ...rest } = previous;
      return rest;
    });
  }, []);

  const fillFilename = ({ filename, values }) => {
    if (filename == null) {
      return filename;
    }
    return filename.replace(/{([^}]+)}/g, (match, key) => {
      return typeof values[key] !== "undefined" ? values[key] : match;
    });
  };

  const downloadTemplate = async ({
    template,
    emailTargetId = null,
    overrideResolvedSubmission = null,
    useOriginalTemplate = false,
    wordDocumentOnly = false,
  }) => {
    const content = makeContent({ overrideResolvedSubmission });

    let filename = fillFilename({
      filename: template.filename,
      values: content,
    });
    if (wordDocumentOnly) {
      filename = `${(filename ?? template.title ?? "template").replace(
        /\.(pdf|docx)$/i,
        ""
      )}.docx`;
    }

    const result = await fillTemplate({
      templateId: template.id,
      templateForm: template.form,
      content: [
        "WORD_JINJA",
        "ENDORSEABLE_WORD_JINJA",
        "ENDORSABLE_PDF",
      ].includes(template.form)
        ? makeJinjaContent({ overrideResolvedSubmission })
        : content,
      emailTargetId,
      filename,
      useOriginalTemplate,
      wordDocumentOnly,
    });

    if (result?.data) {
      recordOutcome(template.id, { warnings: result.data.warnings ?? [] });
    } else if (result?.error) {
      recordOutcome(template.id, { error: result.error });
    }

    if (
      result?.data &&
      !emailTargetId &&
      resolvedSubmission?.layers?.[layerIndex]
    ) {
      const layerId = resolvedSubmission.layers[layerIndex].id;

      let docEnvConfig = [];
      if (template.form.includes("JINJA")) {
        try {
          const summaryResult = await getTemplateSummary({
            templateId: template.id,
            templateForm: template.form,
            content: makeJinjaContent({ overrideResolvedSubmission }),
          });

          if (summaryResult?.data?.evaluations) {
            docEnvConfig = summaryResult.data.evaluations;
          }
        } catch (error) {
          // Continue with empty config if summary fails
        }
      }

      let submissionVersionNumber = null;
      let submissionVersionId = null;
      if (versions && versions.length > 0) {
        submissionVersionNumber = versions.length;
        submissionVersionId = versions[0].id;
      }

      await saveDocument({
        submissionId,
        layerId,
        document: {
          id: `${Date.now()}_${Math.random()}`,
          templateName: template.title,
          formattedFilename: template.title,
          filename,
          type: template.form,
          override: false,
          fileId: null,
          documentKey: result.data.documentKey || null,
          createdBy: userEmail,
          createdAt: new Date().toISOString(),
          createdByName: extractNameFromEmail(userEmail).name,
          submissionVersionId: submissionVersionId,
          submissionVersionNumber: submissionVersionNumber,
          submissionSavedBy: savedBy,
          submissionSavedAt: lastSaved
            ? new Date(lastSaved).toISOString()
            : null,
          docEnvConfig: docEnvConfig,
        },
      });
    }

    return result;
  };

  return {
    downloadTemplate,
    isDownloading,
    isError,
    lastOutcome,
    clearOutcome,
  };
};
